/**
 * 区域管理-房间列表
 */
import type { PageContext } from '@zto/zpage'
import { UI_PAGE_EVENTS } from '@zto/zpage-ui-element'
import dayjs from 'dayjs'
import { roomDialog, batchUpdateDeliverDialog } from './_dialogs'
import { ElLoading } from 'element-plus'
import { commonOptions, OutTypeName, ShipmentStatusName } from '@/config'

export default {
  body: {
    type: 'crud',
    actions: {
      query: {
        api: '/sys/admin/api/region/room/pageList',
        method: 'POST',
        // responseTransform: (res: any) => ({ data: res.list, total: res.total || 0 })
      },
      add: {
        label: '添加',
        api: '/sys/admin/api/region/room/create',
        method: 'POST',
        dialog: roomDialog(false),
        successMessage: '添加成功'
      },
      edit: {
        label: '编辑',
        api: '/sys/admin/api/region/room/update',
        method: 'POST',
        dialog: roomDialog(true),
        successMessage: '保存成功'
      },
      delete: {
        label: '删除',
        api: '/sys/admin/api/region/room/delete',
        method: 'POST',
        payload: { idList: ['${data.row.id}'] },
        message: '确认删除该房间吗？'
      },
      batchDelete: {
        label: '批量删除',
        api: '/sys/admin/api/region/room/delete',
        method: 'POST',
        message: '确认批量删除已选房间吗？',
        payload: async ({ app }: PageContext, ext: any) => {
          const { Message } = app.useMessage()
          const selectedRows = ext?.selection
          if (Array.isArray(selectedRows)) {
            // if (!app.getPageSearchData().isCheck) {
            //   Message.warning('请先按供应商进行筛选后再操作推送！')
            //   return false
            // }
            const len = selectedRows?.length
            if (!len) {
              Message.warning('请至少选择一条数据！')
              return false
            }

            const idList = selectedRows.map((it: any) => it?.id).filter(Boolean)
            return {
              idList
            }
          }
        }
      },
      batchUpdateDeliverable: {
        label: '批量编辑是否可配送',
        api: '/sys/admin/api/region/room/batchUpdateDeliverable',
        method: 'POST',
        dialog: batchUpdateDeliverDialog(),
        payload: async ({ app, data }: PageContext, ext: any) => {
          const { Message } = app.useMessage()
          const selectedRows = ext?.selection
          if (Array.isArray(selectedRows)) {
            // if (!app.getPageSearchData().isCheck) {
            //   Message.warning('请先按供应商进行筛选后再操作推送！')
            //   return false
            // }
            const len = selectedRows?.length
            if (!len) {
              Message.warning('请至少选择一条数据！')
              return false
            }

            const idList = selectedRows.map((it: any) => it?.id).filter(Boolean)
            return {
              idList,
              isDeliverable: data.isDeliverable
            }
          }
        },
        successMessage: '批量修改成功'
      },
      customExport: {
        label: '批量导出',
        customAction: true, // 重要：确保 onAction 被执行
        message: false, // 重要：不显示确认对话框，直接执行
        reload: false, // 导出成功后不重新请求列表接口
        payload: async (ctx: PageContext, ext: any) => {
          const searchParams = ext?.searchParams
          return {
            ...searchParams
          }
        },
        onAction: async (ctx: PageContext, actionCfg: any) => {
          const { app } = ctx
          const { Message } = app.useMessage()

          // 显示全屏loading
          const loadingInstance = ElLoading.service({
            lock: true,
            text: '正在导出数据，请稍候...',
            background: 'rgba(0, 0, 0, 0.7)'
          })

          try {
            const searchParams: any = ctx.data || {}

            let allData: any[] = [] // 存储所有数据

            try {
              const res: any = await app.request({
                url: '/sys/admin/api/region/room/export',
                data: {
                  ...searchParams,
                }
              })
              // 将当前页数据添加到总数据中
              allData = res || []

              // 更新提示信息
              // Message.info(`已查询 ${allData.length} 条数据，继续查询第 ${current} 页...`)
            } catch (pageError: any) {
              // 如果查询出错，停止查询
              console.error(`查询数据时出错:`, pageError)
            }

            // 4. 检查是否有数据可导出
            if (!allData || allData.length === 0) {
              Message.warning('没有可导出的数据')
              return
            }

            // Message.info(`共查询到 ${allData.length} 条数据，正在准备导出...`)

            // 5. 获取列配置
            const columns = [
              { label: '小区编号', prop: 'communityCode' },
              { label: '小区名称', prop: 'communityName' },
              {
                label: '房间号(楼栋+单元+楼层+房间号)',
                prop: 'roomCode',
                formatter: (row: any) => {
                  return [row?.buildingName || '', row?.unitName || '', row?.floorName || '', row?.roomName || ''].filter(Boolean).join('')
                }
              },
              {
                label: '是否可配送',
                prop: 'isDeliverable',
                minWidth: 100,
                formatter: (row: any) => {
                  return commonOptions.yesOrNo.find((it: any) => it.value === row.isDeliverable)?.label || '--'
                }
              },
              {
                label: '创建时间',
                prop: 'gmtCreate',
                formatter: (row: any) => {
                  return row.gmtCreate ? dayjs(row.gmtCreate).format('YYYY-MM-DD HH:mm:ss') : '--'
                }
              }
            ]

            // 6. 格式化数据（应用 formatter）
            const formattedData = allData.map((row: any) => {
              const formattedRow: any = {}
              columns.forEach((col: any) => {
                if (col.formatter) {
                  formattedRow[col.prop] = col.formatter(row)
                } else {
                  formattedRow[col.prop] = row[col.prop] || '--'
                }
              })
              return formattedRow
            })
            console.info('🚀 ~ file:index method:onAction line:216 ----- formattedData', formattedData)

            const exportDataFn = app.useComponentsConfig('xlsx.exportData')

            if (exportDataFn) {
              exportDataFn(formattedData, {
                columns: columns,
                fileName: '房间列表',
                app
              })
            }
            Message.success(`导出成功，共导出 ${formattedData.length} 条数据`)
          } catch (error: any) {
            console.error('导出失败:', error)
            // Message.error(error?.message || '导出失败，请稍后重试')
          } finally {
            // 关闭全屏loading
            loadingInstance.close()
          }
        }
      }
    },
    searchOn: [UI_PAGE_EVENTS.ACTIVATED],
    search: {
      labelWidth: 100,
      items: [
        {
          type: 'fuzzy-select',
          prop: 'communityCode',
          label: '小区',
          optionValueProp: 'communityCode',
          optionLabelProp: 'communityName',
          triggerFocus: true,
          remote: true,
          keywordProp: 'communityName',
          api:'/sys/admin/api/region/community/pageList',
          span: 6
        },
        { type: 'input', prop: 'buildingName', label: '楼栋号', span: 6 },
        { type: 'input', prop: 'unitName', label: '单元号', span: 6 },
        { type: 'input', prop: 'floorName', label: '楼层号', span: 6 },
        { type: 'input', prop: 'roomName', label: '房间号', span: 6 }
      ]
    },
    toolbar: {
      noExport: true,
      items: [
        { action: 'add' },
        { action: 'batchUpdateDeliverable' },
        { action: 'batchDelete' },
        { action: 'customExport' }
      ]
    },
    table: {
      showCheckbox: true,
      innerAttrs: {
        operationPosition: 'left',
        noIndex: false,
        // pageSize: 10,
        // pageSizes: [5, 10, 15, 20],
      },
      operation: {
        width: 120,
        items: [{ action: 'edit' }, { action: 'delete' }]
      },
      columns: [
        { label: '小区编号', prop: 'communityCode', minWidth: 100 },
        { label: '小区名称', prop: 'communityName', minWidth: 140 },
        {
          label: '房间号(楼栋+单元+楼层+房间号)',
          minWidth: 200,
          formatter: (row: any) => {
            return [row?.buildingName || '', row?.unitName || '', row?.floorName || '', row?.roomName || ''].filter(Boolean).join('')
          }
        },
        {
          label: '是否可配送',
          prop: 'isDeliverable',
          minWidth: 100,
          formatter: { name: 'enumStr', options: 'yesOrNo' },
          // formatter: (row: any) => {
          //   return commonOptions.yesOrNo.find((it: any) => it.value === row.isDeliverable)?.label || '--'
          // }
        },
        {
          label: '创建时间',
          prop: 'gmtCreate',
          minWidth: 160,
          formatter: (row: any) => (row?.gmtCreate ? dayjs(row.gmtCreate).format('YYYY-MM-DD HH:mm:ss') : '--')
        }
      ]
    }
  }
}
